/**
 * Referral traffic-source values stored on `wlc_referrals` documents as the
 * `source` field (set at submission time based on which screening URL the
 * prospective patient arrived through).
 *
 * Why this module exists (layman's terms):
 * - Well Life Clinic runs its ADHD Right-to-Choose screening form on more than
 *   one entry point: their own website ("Direct / Organic") and a listing on
 *   the Patient.info website. Each referral records where it came from so we
 *   can report the split between the two.
 * - The provider analytics backend buckets referral metrics by this raw source
 *   string (see `bySource` in
 *   `python-functions/functions/analytics/provider_analytics.py`). The backend
 *   deliberately does NOT send human-readable labels or a filter option list —
 *   those live here so the frontend has ONE source of truth for how sources are
 *   named and ordered, mirroring how `assessmentTypesByOrg.js` owns contract
 *   labels. Keeping labels FE-side means renaming "Patient.info" is a single
 *   edit here rather than a backend deploy.
 *
 * Note on history: referrals submitted before source tracking existed
 * (roughly March–May 2026) have no `source` field. The backend folds those,
 * and any unknown/blank source, into `main` — so every referral is always
 * attributed to exactly one of the keys below.
 */

// The special aggregate bucket. Selecting this in the filter shows every
// referral regardless of source (and, in the export, breaks the figures down
// by source). It is not a real `source` value on any document.
export const REFERRAL_SOURCE_ALL = 'all';

// Raw `source` values as written to Firestore by the WLC screening form.
export const REFERRAL_SOURCE_MAIN = 'main';
export const REFERRAL_SOURCE_PATIENT_INFO = 'nhs-adhd-patient-dot-info';

/**
 * Human-readable label for each raw source key (plus the aggregate). Used for
 * the filter dropdown, the "Referrals by Source" split cards, and the Excel
 * export's Source column. Any source key not present here falls back to its
 * raw string, so an unexpected new source still renders (defensively) rather
 * than showing "undefined".
 */
export const REFERRAL_SOURCE_LABELS = {
  [REFERRAL_SOURCE_ALL]: 'All Sources',
  [REFERRAL_SOURCE_MAIN]: 'Direct / Organic',
  [REFERRAL_SOURCE_PATIENT_INFO]: 'Patient.info',
};

/**
 * Ordered list of the two real traffic sources (excludes the `all` aggregate).
 * Used to render the source-split cards and to drive the per-source columns of
 * the "All" export in a stable, predictable order.
 */
export const REFERRAL_SOURCE_KEYS = [
  REFERRAL_SOURCE_MAIN,
  REFERRAL_SOURCE_PATIENT_INFO,
];

/**
 * Dropdown options for the referral source filter: "All Sources" first, then
 * each real source in `REFERRAL_SOURCE_KEYS` order. Shape matches the other
 * analytics selects ({ value, label }).
 */
export const REFERRAL_SOURCE_OPTIONS = [
  { value: REFERRAL_SOURCE_ALL, label: REFERRAL_SOURCE_LABELS[REFERRAL_SOURCE_ALL] },
  ...REFERRAL_SOURCE_KEYS.map((key) => ({
    value: key,
    label: REFERRAL_SOURCE_LABELS[key] || key,
  })),
];

/** Resolve a raw source key to its display label, defaulting to the raw key. */
export function referralSourceLabel(sourceKey) {
  return REFERRAL_SOURCE_LABELS[sourceKey] || sourceKey;
}
